<script setup>
import logoDark from "@/assets/img/logo-circle-dark.svg?url";
import logoLight from "@/assets/img/logo-circle.svg?url";
import { supabase } from "@/lib/supabaseClient";
import { useUserStore } from "@/stores/user";
import { alertController, IonIcon } from "@ionic/vue";
import {
  compass,
  compassOutline,
  home,
  homeOutline,
  logOutOutline,
  settings,
  settingsOutline,
} from "ionicons/icons";
import { useRoute, useRouter } from "vue-router";

const userStore = useUserStore();
const route = useRoute();
const router = useRouter();

const navItems = [
  {
    label: "Home",
    icon: homeOutline,
    iconActive: home,
    href: "/dashboard",
  },
  {
    label: "Entdecken",
    icon: compassOutline,
    iconActive: compass,
    href: "/recommendations",
  },
  {
    label: "Einstellungen",
    icon: settingsOutline,
    iconActive: settings,
    href: "/settings",
  },
];

const isActive = (href) => {
  return route.path === href || route.path.startsWith(href + "/");
};

const logout = async () => {
  const alert = await alertController.create({
    header: "Abmelden",
    message: "Möchten Sie sich wirklich abmelden?",
    buttons: [
      {
        text: "Abbrechen",
        role: "cancel",
      },
      {
        text: "Bestätigen",
        role: "confirm",
        handler: async () => {
          const { error } = await supabase.auth.signOut({ scope: "local" });
          if (error) {
            const alert = await alertController.create({
              header: "Fehler",
              message: "Beim Abmelden ist ein Fehler aufgetreten.",
              buttons: ["OK"],
            });
            alert.present();
          }
          userStore.user = null;
          router.push("/login");
        },
      },
    ],
  });
  alert.present();
};
</script>

<template>
  <aside class="desktop-sidebar">
    <!-- Logo Section -->
    <div class="sidebar-header">
      <router-link to="/dashboard" class="logo-link">
        <img :src="logoLight" alt="Behalts" class="logo dark:hidden" />
        <img :src="logoDark" alt="Behalts" class="logo hidden dark:block" />
        <span class="text-2xl font-bold text-[#1f7a63] dark:text-[#5fa89a]"
          >Behalts</span
        >
      </router-link>
    </div>

    <!-- Navigation -->
    <nav class="sidebar-nav">
      <ul>
        <li v-for="item in navItems" :key="item.href">
          <router-link
            :to="item.href"
            :class="['nav-item', { active: isActive(item.href) }]"
          >
            <ion-icon
              :icon="isActive(item.href) ? item.iconActive : item.icon"
              class="nav-icon"
            />
            <span class="nav-label">{{ item.label }}</span>
            <span v-if="isActive(item.href)" class="active-indicator"></span>
          </router-link>
        </li>
      </ul>
    </nav>

    <!-- User Section -->
    <div class="sidebar-footer">
      <template v-if="userStore.user">
        <div class="user-info">
          <div class="user-avatar">
            {{ userStore.user.email?.charAt(0).toUpperCase() }}
          </div>
          <div class="user-details">
            <p class="user-email">{{ userStore.user.email }}</p>
          </div>
        </div>
        <div class="user-actions">
          <router-link to="/settings" class="action-btn">
            <ion-icon :icon="settingsOutline" />
          </router-link>
          <button @click="logout" class="action-btn">
            <ion-icon :icon="logOutOutline" />
          </button>
        </div>
      </template>
      <template v-else>
        <router-link to="/login" class="login-btn"> Anmelden </router-link>
      </template>
    </div>
  </aside>
</template>

<style scoped>
.desktop-sidebar {
  display: none;
  position: fixed;
  left: 0;
  top: 0;
  bottom: 0;
  width: 260px;
  background: var(--ion-background-color, #fff);
  border-right: 1px solid var(--ion-border-color, rgba(0, 0, 0, 0.08));
  z-index: 1000;
  flex-direction: column;
}

@media (min-width: 1024px) {
  .desktop-sidebar {
    display: flex;
  }
}

.dark .desktop-sidebar {
  background: var(--ion-background-color, #1a1a1a);
  border-right-color: rgba(255, 255, 255, 0.08);
}

/* Logo Section */
.sidebar-header {
  padding: 1.5rem;
  border-bottom: 1px solid var(--ion-border-color, rgba(0, 0, 0, 0.06));
}

.dark .sidebar-header {
  border-bottom-color: rgba(255, 255, 255, 0.06);
}

.logo-link {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  text-decoration: none;
}

.logo {
  height: 2rem;
}

/* Navigation */
.sidebar-nav {
  flex: 1;
  padding: 1rem;
  overflow-y: auto;
}

.sidebar-nav ul {
  list-style: none;
  margin: 0;
  padding: 0;
  display: flex;
  flex-direction: column;
  gap: 0.25rem;
}

.nav-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1rem;
  border-radius: 0.75rem;
  text-decoration: none;
  color: var(--ion-text-color, #333);
  font-size: 0.9375rem;
  font-weight: 500;
  transition: all 0.15s ease;
  position: relative;
}

.dark .nav-item {
  color: rgba(255, 255, 255, 0.8);
}

.nav-item:hover {
  background: rgba(0, 0, 0, 0.04);
}

.dark .nav-item:hover:not(.active) {
  background: rgba(255, 255, 255, 0.06);
}

.nav-item.active {
  background: linear-gradient(135deg, #1f7a63 0%, #2a9d7f 100%);
  color: #fff;
  box-shadow: 0 4px 12px rgba(31, 122, 99, 0.25);
}

.dark .nav-item.active {
  color: #fff;
}

.nav-icon {
  font-size: 1.375rem;
  flex-shrink: 0;
}

.nav-label {
  flex: 1;
}

.active-indicator {
  position: absolute;
  right: -1rem;
  top: 50%;
  transform: translateY(-50%);
  width: 4px;
  height: 24px;
  background: #1f7a63;
  border-radius: 4px 0 0 4px;
  display: none;
}

/* User Section */
.sidebar-footer {
  padding: 1rem;
  border-top: 1px solid var(--ion-border-color, rgba(0, 0, 0, 0.06));
}

.dark .sidebar-footer {
  border-top-color: rgba(255, 255, 255, 0.06);
}

.user-info {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem;
  margin-bottom: 0.75rem;
}

.user-avatar {
  width: 2.5rem;
  height: 2.5rem;
  border-radius: 50%;
  background: linear-gradient(135deg, #1f7a63 0%, #2a9d7f 100%);
  color: #fff;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1rem;
  flex-shrink: 0;
}

.user-details {
  flex: 1;
  min-width: 0;
}

.user-email {
  margin: 0;
  font-size: 0.8125rem;
  color: var(--ion-text-color, #333);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.dark .user-email {
  color: rgba(255, 255, 255, 0.9);
}

.user-actions {
  display: flex;
  gap: 0.5rem;
}

.action-btn {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0.625rem;
  border-radius: 0.625rem;
  background: rgba(0, 0, 0, 0.04);
  color: var(--ion-text-color, #555);
  text-decoration: none;
  border: none;
  cursor: pointer;
  transition: all 0.15s ease;
  font-size: 1.25rem;
}

.dark .action-btn {
  background: rgba(255, 255, 255, 0.06);
  color: rgba(255, 255, 255, 0.8);
}

.action-btn:hover {
  background: rgba(0, 0, 0, 0.08);
}

.dark .action-btn:hover {
  background: rgba(255, 255, 255, 0.1);
}

.login-btn {
  display: block;
  width: 100%;
  padding: 0.875rem;
  border-radius: 0.75rem;
  background: linear-gradient(135deg, #1f7a63 0%, #2a9d7f 100%);
  color: #fff;
  text-align: center;
  text-decoration: none;
  font-weight: 600;
  font-size: 0.9375rem;
  transition: all 0.15s ease;
  box-shadow: 0 4px 12px rgba(31, 122, 99, 0.2);
}

.login-btn:hover {
  transform: translateY(-1px);
  box-shadow: 0 6px 16px rgba(31, 122, 99, 0.3);
}
</style>
